import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ScanLine, ShieldAlert, CheckCircle2, Fan, Cog, BatteryCharging,
  Frame, Camera, Anchor, RotateCcw,
} from "lucide-react";

// Ticket 5 sample — one preflight inspection interaction for gsw-4 only.
// Reinforces the pilot habit: Look -> Identify -> Assess -> Decide.
const AREAS = [
  {
    key: "propellers",
    label: "Propellers",
    icon: Fan,
    marker: { x: 308, y: 104 },
    inspecting: "The blades that generate lift — most drones have four.",
    condition:
      "Run a finger along every blade edge and tip. They should be smooth, straight, and firmly seated. Check for cracks, chips, nicks, bends, or warping.",
    stop:
      "Any crack, chip, or bend — even a small one. A damaged propeller can shatter at full RPM and cause loss of control.",
    action:
      "Do not fly. Replace it with a matched, undamaged propeller before your flight.",
  },
  {
    key: "motors",
    label: "Motors",
    icon: Cog,
    marker: { x: 92, y: 300 },
    inspecting: "The units beneath each propeller that spin them.",
    condition:
      "Gently spin each motor by hand. It should turn freely and smoothly, and sit firmly with no play. Feel and listen for grit, grinding, or resistance.",
    stop:
      "Grinding, gritty rotation, unusual resistance, or a motor that wobbles or feels loose.",
    action:
      "Do not fly. Have the motor inspected or serviced before returning the aircraft to flight.",
  },
  {
    key: "battery",
    label: "Battery",
    icon: BatteryCharging,
    marker: { x: 200, y: 205 },
    inspecting: "The power source — usually a rechargeable lithium pack.",
    condition:
      "Inspect all sides for swelling, puffiness, dents, punctures, leaks, or frayed wiring. Confirm it is charged and clicks firmly into place.",
    stop:
      "Any swelling or puffiness, physical damage, or a battery that does not seat securely.",
    action:
      "Never fly or charge a swollen or damaged battery. Isolate it safely and follow the manufacturer's guidance for handling and disposal.",
  },
  {
    key: "frame",
    label: "Body / Frame",
    icon: Frame,
    marker: { x: 255, y: 255 },
    inspecting: "The central airframe and the arms that hold everything together.",
    condition:
      "Look closely where the arms meet the body. Arms should be rigid, fasteners tight, and nothing should rattle or flex.",
    stop:
      "Cracks in the frame or arms, loose screws, or flex where the structure should be solid.",
    action:
      "Do not fly. A compromised frame can fail in the air — inspect and repair before use.",
  },
  {
    key: "gimbal",
    label: "Gimbal / Camera",
    icon: Camera,
    marker: { x: 200, y: 150 },
    inspecting: "The stabilized mount and camera at the front of many drones.",
    condition:
      "Remove the gimbal guard or clamp if fitted. Confirm the lens is clean, the gimbal moves freely, and it is not stuck or hanging loose.",
    stop:
      "A gimbal that is jammed, hanging loose, or making unusual noise on start-up — or a guard still attached.",
    action:
      "Resolve it before any flight that depends on stable video, and never take off with the gimbal guard still in place.",
  },
  {
    key: "landing",
    label: "Landing Gear",
    icon: Anchor,
    marker: { x: 150, y: 255 },
    inspecting: "The legs or skids the aircraft rests and lands on.",
    condition:
      "Confirm the legs are secure, even, and undamaged. Folding gear must be fully locked in the flight position.",
    stop:
      "Cracked, bent, loose, or unlocked landing gear.",
    action:
      "Do not fly until the gear is secure and locked — unstable gear risks a tip-over on takeoff or landing.",
  },
];

function DroneDiagram({ activeKey, reviewed, onSelect }) {
  const arms = [
    [200, 200, 92, 104],
    [200, 200, 308, 104],
    [200, 200, 92, 300],
    [200, 200, 308, 300],
  ];
  return (
    <svg viewBox="0 0 400 380" className="w-full h-auto select-none" role="img"
      aria-label="Top-down drone inspection diagram with selectable areas">
      {/* arms */}
      {arms.map((a, i) => (
        <line key={i} x1={a[0]} y1={a[1]} x2={a[2]} y2={a[3]}
          stroke="rgba(148,163,184,0.35)" strokeWidth="14" strokeLinecap="round" />
      ))}
      {/* propeller discs + motors */}
      {[[92, 104], [308, 104], [92, 300], [308, 300]].map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="42" fill="rgba(34,211,238,0.06)" stroke="rgba(34,211,238,0.3)" strokeWidth="1.5" />
          <ellipse cx={x} cy={y} rx="40" ry="9" fill="rgba(34,211,238,0.14)" transform={`rotate(35 ${x} ${y})`} />
          <ellipse cx={x} cy={y} rx="40" ry="9" fill="rgba(34,211,238,0.14)" transform={`rotate(-35 ${x} ${y})`} />
          <circle cx={x} cy={y} r="12" fill="#0b1220" stroke="rgba(148,163,184,0.6)" strokeWidth="2" />
        </g>
      ))}
      {/* body */}
      <rect x="150" y="150" width="100" height="100" rx="16" fill="#0f172a" stroke="rgba(148,163,184,0.55)" strokeWidth="2" />
      {/* front indicator */}
      <path d="M188 150 L200 136 L212 150 Z" fill="rgba(34,211,238,0.6)" />
      {/* battery */}
      <rect x="172" y="182" width="56" height="46" rx="6" fill="rgba(34,211,238,0.08)" stroke="rgba(34,211,238,0.45)" strokeWidth="1.5" />
      <line x1="200" y1="182" x2="200" y2="228" stroke="rgba(34,211,238,0.35)" strokeWidth="1.5" />
      {/* camera / gimbal */}
      <rect x="186" y="150" width="28" height="16" rx="4" fill="#0b1220" stroke="rgba(148,163,184,0.6)" strokeWidth="1.5" />
      <circle cx="200" cy="158" r="4" fill="rgba(34,211,238,0.7)" />
      {/* landing skids */}
      <rect x="128" y="244" width="10" height="30" rx="4" fill="rgba(148,163,184,0.4)" transform="rotate(20 133 259)" />
      <rect x="262" y="244" width="10" height="30" rx="4" fill="rgba(148,163,184,0.4)" transform="rotate(-20 267 259)" />

      {/* hotspot markers */}
      {AREAS.map((a, i) => {
        const on = a.key === activeKey;
        const done = reviewed.includes(a.key);
        return (
          <g key={a.key} onClick={() => onSelect(a.key)} style={{ cursor: "pointer" }}
            data-testid={`preflight-area-${a.key}`} tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(a.key); } }}>
            {on && <circle cx={a.marker.x} cy={a.marker.y} r="18" fill="none" stroke="#22d3ee" strokeWidth="2">
              <animate attributeName="r" values="16;20;16" dur="1.6s" repeatCount="indefinite" />
            </circle>}
            <circle cx={a.marker.x} cy={a.marker.y} r="14"
              fill={on ? "#22d3ee" : done ? "rgba(34,211,238,0.25)" : "#020617"}
              stroke={on ? "#22d3ee" : "rgba(34,211,238,0.7)"} strokeWidth="2" />
            <text x={a.marker.x} y={a.marker.y + 5} textAnchor="middle"
              fontSize="15" fontWeight="700" fill={on ? "#020617" : "#e2e8f0"}
              style={{ pointerEvents: "none", fontFamily: "monospace" }}>{i + 1}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function PreflightInspection() {
  const [activeKey, setActiveKey] = useState(null);
  const [reviewed, setReviewed] = useState([]);

  const select = (key) => {
    setActiveKey(key);
    setReviewed((r) => (r.includes(key) ? r : [...r, key]));
  };
  const reset = () => { setActiveKey(null); setReviewed([]); };

  const active = AREAS.find((a) => a.key === activeKey) || null;
  const allDone = reviewed.length === AREAS.length;

  return (
    <div className="mt-8 rounded-sm border border-primary/25 bg-primary/[0.03] overflow-hidden" data-testid="preflight-inspection">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-primary/20 bg-primary/10">
        <ScanLine className="h-4 w-4 text-primary" />
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Preflight Inspection</span>
      </div>

      <div className="p-5">
        <p className="text-[15px] text-slate-300 leading-relaxed">
          <span className="font-semibold text-foreground">Inspect your drone before every flight.</span> Professional
          pilots follow the same routine each time. Select each area of the aircraft and walk through it:
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] font-mono uppercase tracking-widest">
          {["Look", "Identify", "Assess", "Decide"].map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <span className="text-primary">{s}</span>
              {i < 3 && <span className="text-muted-foreground">→</span>}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5 items-start">
          {/* diagram */}
          <div className="rounded-sm border border-border/60 bg-secondary/20 p-3">
            <DroneDiagram activeKey={activeKey} reviewed={reviewed} onSelect={select} />
            {/* chips (mobile-friendly + accessible) */}
            <div className="flex flex-wrap gap-2 mt-3">
              {AREAS.map((a, i) => {
                const on = a.key === activeKey;
                const done = reviewed.includes(a.key);
                const Icon = a.icon;
                return (
                  <button key={a.key} onClick={() => select(a.key)} data-testid={`preflight-chip-${a.key}`}
                    className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-xs transition-colors ${
                      on ? "border-primary bg-primary/15 text-primary"
                        : done ? "border-primary/40 bg-primary/5 text-slate-300"
                        : "border-border/70 bg-card text-slate-300 hover:border-primary/50"}`}>
                    <span className="font-mono text-[10px] text-muted-foreground">{i + 1}</span>
                    <Icon className="h-3.5 w-3.5" /> {a.label}
                    {done && <CheckCircle2 className="h-3 w-3 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* feedback panel */}
          <div className="min-h-[220px]">
            <AnimatePresence mode="wait">
              {active ? (
                <motion.div key={active.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
                  className="rounded-sm border border-border/60 bg-card" data-testid="preflight-feedback">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60">
                    <active.icon className="h-4 w-4 text-primary" />
                    <span className="font-display font-bold">{active.label}</span>
                  </div>
                  <div className="p-4 space-y-4 text-sm">
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-primary mb-1">
                        <Search className="h-3.5 w-3.5" /> What am I inspecting?
                      </div>
                      <p className="text-slate-300 leading-relaxed">{active.inspecting}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-accent mb-1">
                        <ScanLine className="h-3.5 w-3.5" /> What condition am I looking for?
                      </div>
                      <p className="text-slate-300 leading-relaxed">{active.condition}</p>
                    </div>
                    <div className="rounded-sm border border-red-500/30 bg-red-500/[0.06] p-3">
                      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-red-400 mb-1">
                        <ShieldAlert className="h-3.5 w-3.5" /> What would make me stop?
                      </div>
                      <p className="text-slate-300 leading-relaxed">{active.stop}</p>
                      <p className="text-slate-400 leading-relaxed mt-2 text-[13px]">
                        <span className="text-red-400 font-medium">Safe action: </span>{active.action}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="h-full min-h-[220px] flex flex-col items-center justify-center text-center rounded-sm border border-dashed border-border/60 p-6"
                  data-testid="preflight-empty">
                  <Search className="h-7 w-7 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground max-w-xs">
                    Select a numbered area on the aircraft to begin your inspection walk-through.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* progress + completion */}
        <div className="mt-5 pt-4 border-t border-border/50 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground" data-testid="preflight-progress">
            {reviewed.length} of {AREAS.length} areas reviewed
          </span>
          {reviewed.length > 0 && (
            <button onClick={reset} data-testid="preflight-reset"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> Start over
            </button>
          )}
        </div>

        <AnimatePresence>
          {allDone && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-4 flex items-start gap-3 rounded-sm border border-primary/30 bg-primary/5 p-4" data-testid="preflight-complete">
              <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-slate-200 leading-relaxed">
                <span className="font-semibold text-foreground">Preflight inspection complete.</span> Now inspect your
                own aircraft using the same process — <span className="text-primary">Look, Identify, Assess, Decide</span> — before every flight.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
